import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { RC_DEFAULT, mergeRC } from "@/lib/revenueContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ChevronUp, ChevronDown, Calculator, ExternalLink, Loader2, RotateCcw, Lock, FileText, Plus, Trash2 } from "lucide-react";

const STEP_HINTS = {
  type: "Always first — owners pick their property type here.",
  capacity: "Core inputs — rooms, guests on beds/cots and per-head prices.",
  spaces: "Event space rows. Disabling this also hides Private socials.",
  leisure: "Stream: 365-day potential, 52-weekend guarantee and target slider.",
  weddings: "Stream: venue cost breakdown × weddings per year.",
  socials: "Stream: per-event-space events/year, with food (capacity × plate) and with stay (rooms × rate) options.",
};
const LOCKED = new Set(["type", "capacity"]);

function Field({ label, value, onChange, textarea, type = "text", testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">{label}</span>
      {textarea ? (
        <Textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 text-sm" data-testid={testId} />
      ) : (
        <Input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className="mt-1 text-sm" data-testid={testId} />
      )}
    </label>
  );
}

export default function RevenueCalcCMS() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/admin/content/revenue_calculator")
      .then(({ data }) => setC(mergeRC(data)))
      .catch(() => setC(mergeRC(null)));
  }, []);

  const set = (path, value) => {
    setDirty(true);
    setC((prev) => {
      const next = { ...prev };
      const [a, b] = path;
      next[a] = { ...next[a], [b]: value };
      return next;
    });
  };
  const setStep = (i, key, value) => {
    setDirty(true);
    setC((prev) => ({ ...prev, steps: prev.steps.map((s, j) => (j === i ? { ...s, [key]: value } : s)) }));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 1) return; // "type" stays first
    setDirty(true);
    setC((prev) => {
      if (j >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...prev, steps };
    });
  };

  const setPdfListItem = (key, i, field, value) => {
    setDirty(true);
    setC((prev) => ({ ...prev, pdf: { ...prev.pdf, [key]: (prev.pdf[key] || []).map((x, j) => (j === i ? { ...x, [field]: value } : x)) } }));
  };
  const addPdfListItem = (key, item) => {
    setDirty(true);
    setC((prev) => ({ ...prev, pdf: { ...prev.pdf, [key]: [...(prev.pdf[key] || []), item] } }));
  };
  const removePdfListItem = (key, i) => {
    setDirty(true);
    setC((prev) => ({ ...prev, pdf: { ...prev.pdf, [key]: (prev.pdf[key] || []).filter((_, j) => j !== i) } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/revenue_calculator", c);
      setDirty(false);
      toast.success("Revenue calculator updated — live on the public page");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Reset all calculator content to the original defaults? You still need to Save.")) return;
    setC(mergeRC(null));
    setDirty(true);
  };

  if (!c) return <div className="p-8 text-zinc-400">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="revenue-calc-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Calculator className="h-6 w-6" /> Revenue Calculator</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Edit the copy, defaults and section order of the public calculator.{" "}
            <Link to="/revenue-calculator" target="_blank" className="inline-flex items-center gap-1 text-zinc-800 underline underline-offset-2">
              View page <ExternalLink className="w-3 h-3" />
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="rc-cms-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults</Button>
          <Button onClick={save} disabled={saving} data-testid="rc-cms-save">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save
          </Button>
        </div>
      </div>

      {dirty && <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-md" data-testid="rc-cms-unsaved">Unsaved changes — click Save to publish.</div>}

      {/* Hero */}
      <div className="bg-white border rounded-xl p-5 mb-5" data-testid="rc-cms-hero">
        <div className="text-sm font-semibold mb-4">Hero</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Overline" value={c.hero.overline} onChange={(v) => set(["hero", "overline"], v)} testId="rc-cms-hero-overline" className="sm:col-span-2" />
          <Field label="Headline — before accent" value={c.hero.headline_pre} onChange={(v) => set(["hero", "headline_pre"], v)} testId="rc-cms-hero-pre" />
          <Field label="Headline — accent (italic, clay)" value={c.hero.headline_accent} onChange={(v) => set(["hero", "headline_accent"], v)} testId="rc-cms-hero-accent" />
          <Field label="Headline — after accent" value={c.hero.headline_post} onChange={(v) => set(["hero", "headline_post"], v)} />
          <Field label="Subheadline" textarea value={c.hero.subheadline} onChange={(v) => set(["hero", "subheadline"], v)} className="sm:col-span-2" testId="rc-cms-hero-sub" />
        </div>
      </div>

      {/* Steps */}
      <div className="bg-white border rounded-xl mb-5" data-testid="rc-cms-steps">
        <div className="p-5 pb-3">
          <div className="text-sm font-semibold">Sections — edit & reorder</div>
          <p className="text-xs text-zinc-500 mt-1">Use the arrows to reorder. "Property type" always stays first; core input sections can't be turned off.</p>
        </div>
        {c.steps.map((s, i) => (
          <div key={s.id} className="border-t px-5 py-4" data-testid={`rc-cms-step-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => move(i, -1)} disabled={i <= 1} className="p-0.5 text-zinc-400 hover:text-zinc-900 disabled:opacity-25" aria-label="Move up" data-testid={`rc-cms-up-${s.id}`}><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === 0 || i === c.steps.length - 1} className="p-0.5 text-zinc-400 hover:text-zinc-900 disabled:opacity-25" aria-label="Move down" data-testid={`rc-cms-down-${s.id}`}><ChevronDown className="w-4 h-4" /></button>
              </div>
              <span className="text-xs font-mono text-zinc-400 w-6">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.title}</div>
                <div className="text-[11px] text-zinc-400">{STEP_HINTS[s.id]}</div>
              </div>
              {LOCKED.has(s.id) ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-zinc-400"><Lock className="w-3 h-3" /> Core</span>
              ) : (
                <label className="flex items-center gap-2 text-xs text-zinc-500">
                  Show
                  <Switch checked={s.enabled !== false} onCheckedChange={(v) => setStep(i, "enabled", v)} data-testid={`rc-cms-toggle-${s.id}`} />
                </label>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-3 pl-9">
              <Field label="Section title" value={s.title} onChange={(v) => setStep(i, "title", v)} testId={`rc-cms-title-${s.id}`} />
              <Field label="Section intro" textarea value={s.intro} onChange={(v) => setStep(i, "intro", v)} testId={`rc-cms-intro-${s.id}`} />
              {s.id === "capacity" && (
                <>
                  <Field label="Default price per head — beds (₹)" type="number" value={s.bed_price} onChange={(v) => setStep(i, "bed_price", v)} testId="rc-cms-bed-price" />
                  <Field label="Default price per head — extra cots (₹)" type="number" value={s.cot_price} onChange={(v) => setStep(i, "cot_price", v)} testId="rc-cms-cot-price" />
                </>
              )}
              {s.id === "socials" && (
                <Field label="Default cost per plate (₹)" type="number" value={s.food_per_head} onChange={(v) => setStep(i, "food_per_head", v)} testId="rc-cms-food-price" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary panel */}
      <div className="bg-white border rounded-xl p-5 mb-5" data-testid="rc-cms-summary">
        <div className="text-sm font-semibold mb-4">Summary panel (dark sidebar)</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Overline" value={c.summary.overline} onChange={(v) => set(["summary", "overline"], v)} testId="rc-cms-sum-overline" />
          <Field label="Total label" value={c.summary.total_label} onChange={(v) => set(["summary", "total_label"], v)} testId="rc-cms-sum-total-label" />
          <Field label="Download button label" value={c.summary.download_label} onChange={(v) => set(["summary", "download_label"], v)} testId="rc-cms-sum-download" />
          <Field label="Discuss button label" value={c.summary.discuss_label} onChange={(v) => set(["summary", "discuss_label"], v)} testId="rc-cms-sum-discuss" />
          <Field label="Disclaimer" textarea value={c.summary.disclaimer} onChange={(v) => set(["summary", "disclaimer"], v)} className="sm:col-span-2" testId="rc-cms-sum-disclaimer" />
        </div>
      </div>

      {/* Closing strip */}
      <div className="bg-white border rounded-xl p-5" data-testid="rc-cms-closing">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold">Closing strip</div>
          <label className="flex items-center gap-2 text-xs text-zinc-500">
            Show
            <Switch checked={c.closing.enabled !== false} onCheckedChange={(v) => set(["closing", "enabled"], v)} data-testid="rc-cms-closing-toggle" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Heading" value={c.closing.heading} onChange={(v) => set(["closing", "heading"], v)} testId="rc-cms-closing-heading" />
          <Field label="CTA label" value={c.closing.cta_label} onChange={(v) => set(["closing", "cta_label"], v)} testId="rc-cms-closing-cta" />
          <Field label="Text" textarea value={c.closing.text} onChange={(v) => set(["closing", "text"], v)} className="sm:col-span-2" testId="rc-cms-closing-text" />
        </div>
      </div>

      {/* Projection PDF */}
      <div className="bg-white border rounded-xl p-5 mt-5" data-testid="rc-cms-pdf">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4 text-zinc-500" />
          <div className="text-sm font-semibold">Projection PDF</div>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Fully control the PDF owners receive — labels, Happy Retreats messaging, signature and footer. Every download uses the latest saved copy.
        </p>

        {/* Cover */}
        <div className="border rounded-md p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">Cover</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cover eyebrow (top-right)" value={c.pdf.cover_eyebrow} onChange={(v) => set(["pdf", "cover_eyebrow"], v)} testId="rc-cms-pdf-eyebrow" />
            <label className="flex items-center gap-3 text-sm text-zinc-700 mt-5 sm:mt-6">
              <Switch checked={c.pdf.show_contact_on_cover !== false} onCheckedChange={(v) => set(["pdf", "show_contact_on_cover"], v)} data-testid="rc-cms-pdf-contact-toggle" />
              Show owner&apos;s phone/email on the cover
            </label>
          </div>
        </div>

        {/* Section titles */}
        <div className="border rounded-md p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">Section titles</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="The property" value={c.pdf.section_property} onChange={(v) => set(["pdf", "section_property"], v)} testId="rc-cms-pdf-sec-property" />
            <Field label="Leisure & corporate" value={c.pdf.section_leisure} onChange={(v) => set(["pdf", "section_leisure"], v)} testId="rc-cms-pdf-sec-leisure" />
            <Field label="Weddings" value={c.pdf.section_weddings} onChange={(v) => set(["pdf", "section_weddings"], v)} testId="rc-cms-pdf-sec-weddings" />
            <Field label="Private socials" value={c.pdf.section_socials} onChange={(v) => set(["pdf", "section_socials"], v)} testId="rc-cms-pdf-sec-socials" />
            <Field label="Total band label" value={c.pdf.total_band_label} onChange={(v) => set(["pdf", "total_band_label"], v)} className="sm:col-span-2" testId="rc-cms-pdf-total-label" />
          </div>
        </div>

        {/* Partnership page (PDF page 2) */}
        <div className="border rounded-md p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Partnership page — PDF page 2</div>
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              Show
              <Switch checked={c.pdf.message_enabled !== false} onCheckedChange={(v) => set(["pdf", "message_enabled"], v)} data-testid="rc-cms-pdf-msg-toggle" />
            </label>
          </div>
          <p className="text-[11px] text-zinc-400 mb-3">A dedicated second page: headline, then your stats band, then the "why partner" points. The signature also prints at the end of this page.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Page eyebrow (top-right)" value={c.pdf.message_page_eyebrow} onChange={(v) => set(["pdf", "message_page_eyebrow"], v)} testId="rc-cms-pdf-msg-eyebrow" />
            <Field label="Headline" value={c.pdf.message_headline} onChange={(v) => set(["pdf", "message_headline"], v)} testId="rc-cms-pdf-msg-headline" />
          </div>

          {/* Stats */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Stats band</div>
              <label className="flex items-center gap-2 text-xs text-zinc-500">
                Show
                <Switch checked={c.pdf.stats_enabled !== false} onCheckedChange={(v) => set(["pdf", "stats_enabled"], v)} data-testid="rc-cms-pdf-stats-toggle" />
              </label>
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">Prints as a dark band right below the headline — e.g. "2019 · Established in". Up to 8 stats (4 per row).</p>
            {(c.pdf.stats || []).map((st, i) => (
              <div key={i} className="flex items-end gap-3 mb-2" data-testid={`rc-cms-pdf-stat-${i}`}>
                <div className="w-36">
                  <Field label="Value" value={st.value} onChange={(v) => setPdfListItem("stats", i, "value", v)} testId={`rc-cms-pdf-stat-value-${i}`} />
                </div>
                <div className="flex-1">
                  <Field label="Label" value={st.label} onChange={(v) => setPdfListItem("stats", i, "label", v)} testId={`rc-cms-pdf-stat-label-${i}`} />
                </div>
                <Button variant="outline" size="icon" onClick={() => removePdfListItem("stats", i)} aria-label="Remove stat" data-testid={`rc-cms-pdf-stat-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(c.pdf.stats || []).length < 8 && (
              <Button variant="outline" size="sm" onClick={() => addPdfListItem("stats", { value: "", label: "" })} data-testid="rc-cms-pdf-stat-add">
                <Plus className="w-4 h-4 mr-1" /> Add stat
              </Button>
            )}
          </div>

          {/* Why-partner points */}
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Why-partner points</div>
            <p className="text-[11px] text-zinc-400 mb-3">Each point prints as a numbered block — a short bold title with a paragraph under it. Up to 5.</p>
            {(c.pdf.message_items || []).map((it, i) => (
              <div key={i} className="border rounded-md p-3 mb-3" data-testid={`rc-cms-pdf-item-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-zinc-400">{String(i + 1).padStart(2, "0")}</span>
                  <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => removePdfListItem("message_items", i)} aria-label="Remove point" data-testid={`rc-cms-pdf-item-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Field label="Title (short & bold)" value={it.title} onChange={(v) => setPdfListItem("message_items", i, "title", v)} testId={`rc-cms-pdf-item-title-${i}`} />
                <Field label="Paragraph" textarea value={it.body} onChange={(v) => setPdfListItem("message_items", i, "body", v)} className="mt-2" testId={`rc-cms-pdf-item-body-${i}`} />
              </div>
            ))}
            {(c.pdf.message_items || []).length < 5 && (
              <Button variant="outline" size="sm" onClick={() => addPdfListItem("message_items", { title: "", body: "" })} data-testid="rc-cms-pdf-item-add">
                <Plus className="w-4 h-4 mr-1" /> Add point
              </Button>
            )}
          </div>
        </div>

        {/* Signature */}
        <div className="border rounded-md p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Signature — prints at the end of the partnership page</div>
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              Show
              <Switch checked={c.pdf.signature_enabled !== false} onCheckedChange={(v) => set(["pdf", "signature_enabled"], v)} data-testid="rc-cms-pdf-sig-toggle" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name / team" value={c.pdf.signature_name} onChange={(v) => set(["pdf", "signature_name"], v)} testId="rc-cms-pdf-sig-name" />
            <Field label="Designation" value={c.pdf.signature_designation} onChange={(v) => set(["pdf", "signature_designation"], v)} testId="rc-cms-pdf-sig-designation" />
            <Field label="Email" value={c.pdf.signature_email} onChange={(v) => set(["pdf", "signature_email"], v)} testId="rc-cms-pdf-sig-email" />
            <Field label="Phone" value={c.pdf.signature_phone} onChange={(v) => set(["pdf", "signature_phone"], v)} testId="rc-cms-pdf-sig-phone" />
          </div>
        </div>

        {/* Disclaimer + Footer */}
        <div className="border rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">Disclaimer & footer</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Disclaimer" textarea value={c.pdf.disclaimer} onChange={(v) => set(["pdf", "disclaimer"], v)} className="sm:col-span-2" testId="rc-cms-pdf-disclaimer" />
            <Field label="Footer — brand name" value={c.pdf.footer_brand} onChange={(v) => set(["pdf", "footer_brand"], v)} testId="rc-cms-pdf-footer-brand" />
            <Field label="Footer — URL / caption" value={c.pdf.footer_url_line} onChange={(v) => set(["pdf", "footer_url_line"], v)} testId="rc-cms-pdf-footer-url" />
            <Field label="Footer — tagline (right)" value={c.pdf.footer_tagline} onChange={(v) => set(["pdf", "footer_tagline"], v)} className="sm:col-span-2" testId="rc-cms-pdf-footer-tagline" />
          </div>
        </div>
      </div>
    </div>
  );
}
